import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInsuranceStore } from 'src/store/insurance';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { usePaymentStore } from '@features/onboarding/components/7-finish/store/payment';
import { usePlan } from '@features/onboarding/hooks/usePlan';

import { ContractStep } from './components/contract-step';
import { CoverageReassurance } from './components/coverage-reassurance/CoverageReassurance';
import { CoverageReassuranceWithExcuse } from './components/coverage-reassurance-with-excuse/CoverageReassuranceWithExcuse';
import { ExcuseStep } from './components/excuse-step/ExcuseStep';
import { UnderwritingResult } from './UnderwritingResult';
import { PaymentStep } from './components/stripe-payment/PaymentStep';
import { AddressStep } from './components/address-step/AddressStep';
import { NoPlansSelected } from './components/no-plans-selected/NoPlansSelected';

import { cn } from '@utils/index';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { useApplicationStore } from 'src/store/application';
import { updateAddress } from 'src/services/profile';
import { UserAddress } from 'src/services/interfaces/profile.interface';

const components = {
  PaymentStep,
  CoverageReassurance,
  CoverageReassuranceWithExcuse,
  UnderwritingResult,
  ExcuseStep,
  ContractStep,
  AddressStep,
  NoPlansSelected,
} as any;

type EmptyStateVariant =
  | 'default'
  | 'browsing'
  | 'maxedOut'
  | 'rejectedCoverage';

const NO_PLANS_COPY: Record<
  EmptyStateVariant,
  { title: string; description: string; helper?: string }
> = {
  default: {
    title: "Oops — you didn't pick a plan.",
    description: "It's like getting to checkout with an empty cart.",
    helper: 'Click the Previous button and choose a plan or two, or three.',
  },
  browsing: {
    title: 'Just browsing? Totally fine.',
    description:
      "Our doors are always open. Come back and explore when you're ready.",
  },
  maxedOut: {
    title: 'You’re maxed out on awesome!',
    description:
      'Looks like you already have every available plan. No need to add more - you’ve reached peak coverage!',
  },
  rejectedCoverage: {
    title: 'You’ve made some wise choices.',
    description:
      'The plans you selected still offer solid benefits and peace of mind.',
  },
};

const DynamicComponent = ({
  componentName,
  handleNextCompatible,
  setContainerClassName,
  handlePrev,
  isSSNValid,
  setIsSSNValid,
  setIsAddressValid,
  componentProps = {},
}: any) => {
  const Component = components[componentName];

  // Для PaymentStep передаем onSubmit как handleNextCompatible
  if (componentName === 'PaymentStep') {
    return (
      <Component
        {...componentProps}
        onSubmit={handleNextCompatible}
        setContainerClassName={setContainerClassName}
      />
    );
  }

  // Для AddressStep передаем дополнительный пропс для валидации SSN
  if (componentName === 'AddressStep') {
    return (
      <Component
        {...componentProps}
        handleNextCompatible={handleNextCompatible}
        setContainerClassName={setContainerClassName}
        handlePrev={handlePrev}
        onSSNValidationChange={setIsSSNValid}
        onValidationChange={setIsAddressValid}
      />
    );
  }

  return (
    <Component
      {...componentProps}
      handleNextCompatible={handleNextCompatible}
      setContainerClassName={setContainerClassName}
      handlePrev={handlePrev}
    />
  );
};

export const Finish = () => {
  const navigate = useNavigate();
  const { data, setFinishStepKey } = useOnboardingStore();
  const { isLocalLoading, handlePreviousStep, handleJumpTo } =
    useFormControls();
  const { plans: chosenPlans } = usePlan();
  const plans = useInsuranceStore((state) => state.plans);
  const [showPayment, setShowPayment] = useState(false);
  const [showConfirmSign, setShowConfirmSign] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [isOkReject, setIsOkReject] = useState(false);
  const [isSSNValid, setIsSSNValid] = useState(false);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const {
    signedAt,
    activeSubscriptionPlans,
    isAllRejected,
    isMainPlanRejected,
  } = useApplicationStore();

  const medicalPlans = React.useMemo(
    () => chosenPlans.filter((plan) => plan.baseKey === 'majormedical'),
    [chosenPlans]
  );
  const otherPlans = React.useMemo(
    () => chosenPlans.filter((plan) => plan.baseKey !== 'majormedical'),
    [chosenPlans]
  );
  const firstName = React.useMemo(() => {
    const primary = data?.applicants?.find((a) => a.applicant === 'primary');
    return primary?.firstName || '';
  }, [data]);
  const hasSelectedMedicalPlan = medicalPlans.length > 0;
  const hasOtherSelectedPlans = otherPlans.length > 0;
  const {
    isUnderwritingLoading,
    underwritingResult,
    isSubmittingApplication,
    submitFinalApplication,
  } = useFinalSubmissionStore();
  const [isUpdateApplicationLoading, setIsUpdateApplicationLoading] =
    useState(false);

  const { paymentMethodId } = usePaymentStore();

  const hasAnyPlan = !!chosenPlans.length;
  const hasActiveSubscriptions = React.useMemo(
    () => (activeSubscriptionPlans?.length ?? 0) > 0,
    [activeSubscriptionPlans]
  );
  const noPlansVariant = React.useMemo<EmptyStateVariant>(() => {
    if (!hasAnyPlan) {
      if (isAllRejected) {
        return isMainPlanRejected ? 'rejectedCoverage' : 'maxedOut';
      }
      if (
        (underwritingResult?.status === 'rejected' || isMainPlanRejected) &&
        hasActiveSubscriptions
      ) {
        return 'rejectedCoverage';
      }
      if (hasActiveSubscriptions) {
        return 'browsing';
      }
    }

    return 'default';
  }, [
    hasAnyPlan,
    hasActiveSubscriptions,
    isAllRejected,
    underwritingResult?.status,
    isMainPlanRejected,
  ]);
  const noPlansCopy = NO_PLANS_COPY[noPlansVariant];

  const medicalPriceInfo = React.useMemo(() => {
    if (!hasSelectedMedicalPlan) return null;
    const selectedMedical = medicalPlans[0];
    // выбранный план в списке всех планов по uid
    const fullPlan = plans.find((p) => p.uid === (selectedMedical as any)?.id);
    if (!fullPlan) return null;
    const originalPrice = fullPlan.averagePrice;
    const currentPrice = (selectedMedical as any)?.price ?? 0;
    return {
      originalPrice,
      currentPrice,
      isHigherPrice: currentPrice > originalPrice,
    };
  }, [hasSelectedMedicalPlan, medicalPlans, plans]);

  useEffect(() => {
    if (!paymentMethodId) return;
    if (
      underwritingResult?.status === 'rejected' &&
      !isOkReject &&
      !hasOtherSelectedPlans
    )
      return;

    const updatedFormData = useOnboardingStore.getState().data;
    const applicationData = transformApplicationData(updatedFormData, plans);

    const filteredApplicationData = (() => {
      if (underwritingResult?.status !== 'rejected') return applicationData;

      const insurancePlanById = new Map<number, (typeof plans)[number]>(
        plans
          .filter((p) => typeof p.id === 'number')
          .map((p) => [p.id as number, p])
      );

      const nonMedicalPlans = applicationData.plans.filter((p) => {
        const src = insurancePlanById.get(p.id);
        return src ? src.category !== 'Major Medical' : true;
      });

      return { ...applicationData, plans: nonMedicalPlans };
    })();

    const startFinalRequest = async (token: string) => {
      await submitFinalApplication(filteredApplicationData, token);
      navigate('/thanks');
    };

    startFinalRequest(paymentMethodId);
  }, [
    paymentMethodId,
    isOkReject,
    underwritingResult?.status,
    plans,
    submitFinalApplication,
    navigate,
    hasOtherSelectedPlans,
  ]);

  type FinishUI = {
    title?: string;
    description?: string;
    nextButtonLabel?: string;
    hideNextButton?: boolean;
    key?: string;
    componentName:
      | 'PaymentStep'
      | 'CoverageReassurance'
      | 'CoverageReassuranceWithExcuse'
      | 'UnderwritingResult'
      | 'ExcuseStep'
      | 'ContractStep'
      | 'AddressStep'
      | 'NoPlansSelected';
    componentProps?: Record<string, unknown>;
  };
  console.log('paymentMethodId:', paymentMethodId);
  const ui: FinishUI = React.useMemo(() => {
    // Handle case when no plans are selected
    if (!hasAnyPlan) {
      return {
        title: noPlansCopy.title,
        description: noPlansCopy.description,
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'NoPlansSelected',
        key: 'noPlansSelected',
        componentProps: { copy: noPlansCopy },
      };
    }

    if (showPayment) {
      return {
        title: 'Securely connect & pay.',
        description:
          "In this step, you'll set up your ACH payment by linking your bank account through our secure system. It's fast, safe, and once connected, future payments are automatic — no extra steps needed.",
        nextButtonLabel: 'Submit',
        hideNextButton: true,
        componentName: 'PaymentStep',
        key: 'payment',
      };
    }
    if (
      underwritingResult?.status === 'accepted' ||
      underwritingResult?.status === undefined
    ) {
      // Show address step before contract
      if (showAddress && !signedAt) {
        return {
          title: 'Verify your identity securely.',
          description:
            'Your address lets us mail your ID card and plan documents, and your SSN is required to help verify your identity. We take your privacy seriously. <b>Your data is encrypted in transit and at rest, strictly access-controlled, and never sold.</b>',
          nextButtonLabel: hasSelectedMedicalPlan ? 'Sign & Pay' : 'Payment',
          hideNextButton: !(isSSNValid && isAddressValid),
          componentName: 'AddressStep',
          key: 'address',
        };
      }
      // Skip contract step if already signed
      if (showConfirmSign && !signedAt) {
        return {
          title: 'Contract.',
          description: '',
          // description:
          //   "You're almost at the finish line! Review and e-sign our standard insurance contract—it only takes a minute. If now's not a good time, no worries—we've also emailed you a link so you can sign later. Once it's signed, you'll just set up your payment details and you're all set.",
          nextButtonLabel: 'Payment',
          hideNextButton: true,
          componentName: 'ContractStep',
          key: 'contract',
        };
      }
      if (!hasSelectedMedicalPlan && hasOtherSelectedPlans) {
        return {
          // title: `${firstName ? firstName + ', ' : ''}your plan summary is ready!`,
          title: 'Let’s review things.',
          description: `Here’s a quick look at what you’ve chosen. Take a moment to confirm everything looks right before moving on.
          Once you’re ready, the next step is simple: just set up your payment, and you’ll be done!`,
          nextButtonLabel: 'Confirm',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
      if (hasSelectedMedicalPlan && hasOtherSelectedPlans && !signedAt) {
        const descriptionMedicalPlan = `Here’s a quick look at what you’ve chosen. Take a moment to confirm everything looks right before moving on.
        Once you’re ready, the next steps are simple: review your contract and set up your payment, and you’ll be done!`;

        // const otherPlanName = otherPlans.length === 1 ? otherPlans[0]?.name : null;
        // const sameCopy = otherPlanName
        //   ?
        //   'You’ve made some smart choices\n\n' +
        //   'Here’s the great news:\n' +
        //   `<ul class="ul-with-point mt-2"><li class="!font-secondary">The quote for the ${otherPlanName} remains the same.</li>` +
        //   '<li class="!font-secondary">Even better — your healthcare plan came in right on target. The final quote matches what we first showed you. No surprises, just the coverage you expected.</li></ul>\n' +
        //   'Curious about alternatives? Every health plan now comes with your own personalized quote, so you can explore with confidence.'
        //   :
        //   'You’ve made some smart choices\n\n' +
        //   'Here’s the great news:\n' +
        //   '<ul class="ul-with-point mt-2"><li class="!font-secondary">The quote for every plan you selected, except the health plan, remains the same.</li>' +
        //   '<li class="!font-secondary">Even better — your healthcare plan came in right on target. The final quote matches what we first showed you. No surprises, just the coverage you expected.</li></ul>\n' +
        //   'Curious about alternatives? Every health plan now comes with your own personalized quote, so you can explore with confidence.';
        // const higherCopy =
        //   'You\'ve made some great choices\n\n' +
        //   'The good news: the quote for any plan, other than your health plan, remains the same.\n\n' +
        //   'Regarding your health plan quote – After reviewing your application, your final monthly price is a little higher than the estimate. We calculate this based on the answers you supplied in your personal health questionnaire.\n\n' +
        //   'Want to explore other options? No problem—every health plan now displays a personalized quote.';

        return {
          title: 'Let’s review things.',
          description: descriptionMedicalPlan,
          // description:
          //   medicalPriceInfo?.isHigherPrice
          //     ? higherCopy
          //     : sameCopy,
          nextButtonLabel: 'Confirm & Sign',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
      if (hasSelectedMedicalPlan && !hasOtherSelectedPlans && !signedAt) {
        const descriptionMedicalPlan = `Here’s a quick look at what you’ve chosen. Take a moment to confirm everything looks right before moving on.
        Once you’re ready, the next steps are simple: review your contract and set up your payment, and you’ll be done!`;
        const descriptionOtherPlans = `Here’s a quick look at what you’ve chosen. Take a moment to confirm everything looks right before moving on.
        Once you’re ready, the next step is simple: just set up your payment, and you’ll be done!`;
        // const sameCopy =
        //   'You’re all set with your coverage — here’s your final monthly price.\n\n' +
        //   'Great news — the final quote for the healthcare plan you chose is exactly the same as what we first showed you! No surprises here, just the coverage you expected.\n\n' +
        //   'Want to explore other options? No problem—every health plan now displays a personalized quote.';
        // const higherCopy =
        //   'You’re all set with your coverage — here’s your final monthly price.\n\n' +
        //   'Regarding your health plan quote – After reviewing your application, your final monthly price is a little higher than the estimate. We calculate this based on the answers you supplied in your personal health questionnaire.\n\n' +
        //   'Want to explore other options? No problem—every health plan now displays a personalized quote.';

        return {
          // title: `${firstName ? firstName + ', ' : ''}your plan summary is ready!`,
          title: 'Let’s review things.',
          description: hasSelectedMedicalPlan
            ? descriptionMedicalPlan
            : descriptionOtherPlans,
          // description:
          //   medicalPriceInfo?.isHigherPrice
          //     ? higherCopy
          //     : sameCopy,
          nextButtonLabel: 'Confirm & Sign',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
    }

    if (underwritingResult?.status === 'rejected') {
      const availableOtherPlans = plans.filter(
        (p) => p.baseKey !== 'majormedical'
      );
      const userSelectedAllOtherPlans =
        otherPlans.length === availableOtherPlans.length;

      if (userSelectedAllOtherPlans) {
        return {
          title: 'You’ve made some wise choices.',
          description:
            'The plans you selected still offer solid benefits and peace of mind.',
          nextButtonLabel: 'Payment',
          hideNextButton: false,
          componentName: 'CoverageReassuranceWithExcuse',
          key: 'coverageReassuranceWithExcuse',
        };
      }

      return {
        title: 'You Still Have a Path to Coverage!',
        description:
          "We can't offer you one of our health plans this time, but that doesn't mean the search is over.\n\n" +
          'You may qualify for an ACA plan...',
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'ExcuseStep',
        key: 'excuse',
      };
    }

    return {
      title: "We're finalizing your quote.",
      description:
        'Once you’re ready, the next steps are simple: review your contract and set up your payment, and and you’’ll be done!',
      // "You've been approved for your medical insurance, and your final premium has been displayed and locked in!\n\n" +
      // "But remember, you're not covered yet! When you click Finish Up, you'll be taken to a page where you can enter your payment info. Once that's done, we'll send you an email with your plan start dates and a link to your personal Health Portal. From there, you can download insurance cards, find doctors, manage billing, and get in touch with customer service anytime.\n\n" +
      // 'Great job taking this important step toward protecting your health!',
      nextButtonLabel:
        hasSelectedMedicalPlan && hasSelectedMedicalPlan && !signedAt
          ? 'Confirm & Sign'
          : 'Payment',
      hideNextButton: false,
      componentName: 'CoverageReassurance',
      key: 'coverageReassurance',
    };
  }, [
    underwritingResult,
    hasSelectedMedicalPlan,
    hasOtherSelectedPlans,
    firstName,
    showConfirmSign,
    showPayment,
    showAddress,
    signedAt,
    medicalPriceInfo,
    otherPlans,
    isSSNValid,
    isAddressValid,
  ]);

  const {
    title,
    description,
    nextButtonLabel,
    hideNextButton,
    componentName,
    key,
    componentProps,
  } = ui;
  const { currentApplicationId, updateApplicationById, applications } =
    useApplicationStore();

  useEffect(() => {
    setFinishStepKey(key ?? null);
    return () => {
      setFinishStepKey(null);
    };
  }, [key, setFinishStepKey]);

  const handlePrev = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    if (showPayment) {
      setShowPayment(false);
      return;
    }
    if (showConfirmSign) {
      setShowConfirmSign(false);
      setShowAddress(true);
      return;
    }
    if (showAddress) {
      setShowAddress(false);
      return;
    }
    if (underwritingResult?.status === 'rejected') {
      useOnboardingStore.getState().removeMedicalPlan();
    }
    if (underwritingResult?.status === 'rejected' && !hasOtherSelectedPlans) {
      handleJumpTo(FormStepSections.Dental);
      return;
    }
    if (
      underwritingResult?.status === 'rejected' &&
      !isOkReject &&
      hasOtherSelectedPlans
    ) {
      setIsOkReject(true);
      return;
    }
    handlePreviousStep();
  };

  const handleNextCompatible = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    if (componentName === 'AddressStep') {
      // Проверяем валидность формы адреса перед переходом
      if (!isSSNValid || !isAddressValid) {
        console.log('Address form is not valid, cannot proceed');
        return;
      }

      setIsUpdateApplicationLoading(true);
      try {
        console.log(
          'currentApplicationId',
          currentApplicationId,
          'applications',
          applications
        );
        if (currentApplicationId) {
          if (applications[0]) {
            await updateApplicationById(currentApplicationId, applications[0]);
            const { address } = applications[0];
            if (address) {
              await updateAddress(address as UserAddress);
            }
          }
        }
      } catch (error) {
        console.error('Error updating application:', error);
      } finally {
        setIsUpdateApplicationLoading(false);
        setShowAddress(false);
        if (hasSelectedMedicalPlan) {
          setShowConfirmSign(true);
        } else {
          setShowPayment(true);
        }
      }
      return;
    }

    if (nextButtonLabel === 'Payment') {
      setShowAddress(false);
      setShowConfirmSign(false);
      setShowPayment(true);
      return;
    }
    if (componentName === 'CoverageReassurance') {
      setShowAddress(true);
      return;
    }
    if (nextButtonLabel === 'Continue to Sign') {
      setShowAddress(false);
      setShowConfirmSign(true);
      return;
    }
    if (underwritingResult?.status === 'rejected' && isOkReject) {
      setShowPayment(true);
      return;
    }
  };

  const shouldShowSubmitText = () => {
    if (isUnderwritingLoading || isSubmittingApplication) return false;

    if (!hasAnyPlan) {
      return showPayment;
    }

    return false;
  };

  useEffect(() => {
    setIsOkReject(false);
  }, []);

  console.log('componentName', componentName);

  return (
    <OnboardingFormStepLayout
      handleFormSubmit={handleNextCompatible}
      onPrev={handlePrev}
      title={title}
      description={description}
      hideNextButton={hideNextButton}
      isSubmitText={!!shouldShowSubmitText()}
      onlyImage={false}
      // isLocalLoading - TODO не меняется PV-261
      isLoading={
        isUnderwritingLoading ||
        isSubmittingApplication ||
        isLocalLoading ||
        isUpdateApplicationLoading
      }
      nextButtonLabel={nextButtonLabel}
      headerAnimationKey={key}
    >
      <div className={cn('h-full w-full rounded-2xl')}>
        <div className="pb-4 h-full w-full justify-center">
          <DynamicComponent
            handlePrev={handlePrev}
            handleNextCompatible={handleNextCompatible}
            componentName={componentName}
            componentProps={componentProps}
            isSSNValid={isSSNValid}
            setIsSSNValid={setIsSSNValid}
            setIsAddressValid={setIsAddressValid}
          />
        </div>
      </div>
    </OnboardingFormStepLayout>
  );
};
